import { type ExposureHazard } from "@/features/pe_aldd";
import { type PeaSummaryRow } from "@/store/services/facades/types/pea";

export const EXPOSURE_RETURN_PERIODS = ["20", "50", "100", "200", "500", "1000", "1500"] as const;
export const FOCAL_YEAR_SLOTS = [1, 2, 3, 4] as const;
export const FOCAL_SCENARIO_SLOTS = [1, 2] as const;
export const PRIMARY_YEAR = "primary_year" as const;
export const PRIMARY_SCENARIO = "primary_scenario" as const;
export const FOCAL_SCENARIO_LABEL_VARIANTS = {
  long: { value: "medium", label: "Long" },
  gwl: { value: "gwl", label: "Equiv GWL" },
} as const;
export const SERIES_METRICS = {
  damage: {
    key: "avg_damage_mtd",
    fullName: "Maximum-to-Date Value-at-Risk",
    acronym: "MVAR",
    label: "MVAR",
    heading: "Structural Damage (MVAR%)",
  },
  failure: {
    key: "avg_failure_mtd",
    fullName: "Failure Probability",
    acronym: "FP",
    label: "Failure Probability",
    heading: "Operational Disruption (FP%)",
  },
  productivity_loss: {
    key: "productivity_loss",
    fullName: "Productivity Loss",
    acronym: "PL",
    label: "Productivity Loss",
    heading: "Operational Downtime (PL%)",
  },
  ttip: {
    key: "ttip",
    fullName: "Total Technical Insurance Premium",
    acronym: "TTIP",
    label: "TTIP",
    heading: "Annual Damage Cost (TTIP$)",
  },
} as const satisfies Record<
  string,
  { key: keyof PeaSummaryRow; fullName: string; acronym: string; label: string; heading: string }
>;

// TTIP for a single asset. Same data as `ttip`, so it is a name rather than a metric of its own.
export const TIP_METRIC = {
  fullName: "Technical Insurance Premium",
  acronym: "TIP",
  heading: "Annual Damage Cost (TIP$)",
} as const;

export const EXPOSURE_STATES = {
  exposed: "E",
  unexposed: "U",
  unknown: "?",
} as const;
export const FOCAL_YEAR_HORIZON_LABELS: Record<FocalYearKey, string> = {
  focal_year_1: "Current Year",
  focal_year_2: "Short Term",
  focal_year_3: "Medium Term",
  focal_year_4: "Long Term",
};

const HORIZON_SUFFIX = "_horizon";

/** The metrics as `{ label, value }` options, for selects and checkbox groups. */
export const METRIC_OPTIONS: Array<{ label: string; value: SeriesMetric }> = (
  Object.keys(SERIES_METRICS) as Array<SeriesMetric>
).map((metric) => ({ label: SERIES_METRICS[metric].label, value: metric }));

export type ReportFloodExposure = {
  points: number;
  exposed: Record<FloodExposureHazard, Record<ExposureReturnPeriod, number>>;
};

export type SeriesMetric = keyof typeof SERIES_METRICS;

// The metrics assets are ranked by, in the Top 10 table and re-ranked Top Asset values. TTIP isn't one.
export const RANKING_METRICS = [
  "damage",
  "failure",
  "productivity_loss",
] as const satisfies ReadonlyArray<SeriesMetric>;
export type RankingMetric = (typeof RANKING_METRICS)[number];

export const isRankingMetric = (metric: unknown): metric is RankingMetric => {
  return (RANKING_METRICS as ReadonlyArray<unknown>).includes(metric);
};
export type ExposureReturnPeriod = (typeof EXPOSURE_RETURN_PERIODS)[number];
export type ExposureState = (typeof EXPOSURE_STATES)[keyof typeof EXPOSURE_STATES];
export type FloodExposureHazard = ExposureHazard | "combined";
export type ReportLabels = Record<string, string>;

export type ValuesByFocalYear<TFocalYear> = Record<FocalYearKey, TFocalYear>;
export type ValuesByFocalScenario<TFocalYear> = Record<FocalScenarioKey, TFocalYear>;

export type ReturnPeriodKey = `return_period_${(typeof EXPOSURE_RETURN_PERIODS)[number]}`;
export type FocalYearSelection = FocalYearKey | typeof PRIMARY_YEAR;
export type FocalYearKey = `focal_year_${(typeof FOCAL_YEAR_SLOTS)[number]}`;
export type FocalScenarioKey = `focal_scenario_${(typeof FOCAL_SCENARIO_SLOTS)[number]}`;
export type FocalScenarioSelection = FocalScenarioKey | typeof PRIMARY_SCENARIO;
export type FocalYearHorizonKey = `${FocalYearSelection}${typeof HORIZON_SUFFIX}`;

export const getReturnPeriodKey = (period: ExposureReturnPeriod) => `return_period_${period}` as ReturnPeriodKey;
export const getHorizonKey = (key: FocalYearSelection) => `${key}${HORIZON_SUFFIX}` as FocalYearHorizonKey;
export const getHorizonYear = (key: string) =>
  key.endsWith(HORIZON_SUFFIX) ? (key.slice(0, -HORIZON_SUFFIX.length) as FocalYearSelection) : undefined;
export const getFocalYearSelection = (key: string) =>
  key === PRIMARY_YEAR || FOCAL_YEAR_SLOTS.some((slot) => key === `focal_year_${slot}`)
    ? (key as FocalYearSelection)
    : undefined;
export const getFocalScenarioKey = (index: number) => `focal_scenario_${index + 1}` as FocalScenarioKey;
export const getFocalYearKey = (index: number) => `focal_year_${index + 1}` as FocalYearKey;

export const byFocalYear = <TFocalYear>(values: Array<TFocalYear>): ValuesByFocalYear<TFocalYear> => {
  return Object.fromEntries(
    values.map((value, index) => [getFocalYearKey(index), value])
  ) as ValuesByFocalYear<TFocalYear>;
};
